export type BatchJobWorkflowState =
  | 'draft'
  | 'pending-approval'
  | 'pending-merge'
  | 'warning'
  | 'canceled'
  | 'error'
  | 'complete';

export type BatchJobSubjobType = 'approval' | 'merge';

export type BatchJobSubjobStatus = 'in-progress' | 'error' | 'complete';

export type BatchJobSubjob = {
  id: string;
  type: BatchJobSubjobType;
  status: BatchJobSubjobStatus;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
};

export type BatchJobWorkflowStepStatus =
  | 'pending'
  | 'running'
  | 'complete'
  | 'error'
  | 'skipped'
  | 'warning';

export type BatchJobWorkflowStep = {
  id: string;
  label: string;
  status: BatchJobWorkflowStepStatus;
  log?: string;
};

export type BatchJobWorkflowItem = {
  id: string;
  title: string;
  operation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  workflowState: BatchJobWorkflowState;
  workflowBusy?: 'approval' | 'merge';
  status?: string;
  approvedBy?: string;
  mergedBy?: string;
  request?: {
    title?: string;
    body?: string;
    draft?: boolean;
  };
  totals: {
    queued: number;
    running: number;
    skipped: number;
    prOpened: number;
    manualReview: number;
    failed: number;
    merged: number;
  };
};

export type BatchJobWorkflowFilter = 'all' | BatchJobWorkflowState;

export const WORKFLOW_STATE_LABELS: Record<BatchJobWorkflowState, string> = {
  draft: 'Draft',
  'pending-approval': 'Pending approval',
  'pending-merge': 'Pending merge',
  warning: 'Warning',
  canceled: 'Canceled',
  error: 'Error',
  complete: 'Complete',
};

export type BatchJobChangePreview = {
  headline: string;
  operationLabel: string;
  repositoryCount: number;
  pullRequestCount: number;
  changes: string[];
  warnings?: string[];
};

export type BatchJobWorkflowActionKind = 'approve' | 'merge';

export type BatchJobWorkflowAction = {
  label: string;
  kind: 'opening' | BatchJobWorkflowActionKind | BatchJobWorkflowState;
  mode: 'action' | 'status';
  action?: BatchJobWorkflowActionKind;
  enabled: boolean;
  disabledReason?: string;
  loading: boolean;
  buttonColor: 'primary' | 'default' | 'secondary';
  buttonVariant: 'contained' | 'outlined';
};

export const SUBJOB_TYPE_LABELS: Record<BatchJobSubjobType, string> = {
  approval: 'Approval',
  merge: 'Merge',
};

export function formatBatchUserRef(userRef: string): string {
  const slash = userRef.lastIndexOf('/');
  return slash >= 0 ? userRef.slice(slash + 1) : userRef;
}

export function getActiveSubjob(job: BatchJobWorkflowItem): BatchJobSubjob | undefined {
  if (job.workflowBusy === 'approval') {
    return {
      id: 'approval',
      type: 'approval',
      status: 'in-progress',
      startedAt: job.updatedAt,
    };
  }
  if (job.workflowBusy === 'merge') {
    return {
      id: 'merge',
      type: 'merge',
      status: 'in-progress',
      startedAt: job.updatedAt,
    };
  }
  return undefined;
}

export function matchesWorkflowFilter(
  job: BatchJobWorkflowItem,
  filter: BatchJobWorkflowFilter,
): boolean {
  if (filter === 'all') {
    return true;
  }
  return job.workflowState === filter;
}

export function canApproveJob(job: BatchJobWorkflowItem, currentUserRef?: string): boolean {
  if (!currentUserRef || getActiveSubjob(job)) {
    return false;
  }
  return job.workflowState === 'pending-approval' && job.createdBy !== currentUserRef;
}

export function canMergeJob(job: BatchJobWorkflowItem, currentUserRef?: string): boolean {
  if (!currentUserRef || getActiveSubjob(job)) {
    return false;
  }
  return (
    (job.workflowState === 'pending-merge' || job.workflowState === 'warning') &&
    job.createdBy !== currentUserRef &&
    job.totals.prOpened > 0
  );
}

export function approvalDisabledReason(
  job: BatchJobWorkflowItem,
  currentUserRef?: string,
): string | undefined {
  if (getActiveSubjob(job)) {
    return 'Wait for the current step to finish';
  }
  if (job.workflowState !== 'pending-approval') {
    return 'Only jobs pending approval can be approved';
  }
  if (currentUserRef && job.createdBy === currentUserRef) {
    return 'You cannot approve a job you created';
  }
  return undefined;
}

export function mergeDisabledReason(
  job: BatchJobWorkflowItem,
  currentUserRef?: string,
): string | undefined {
  if (getActiveSubjob(job)) {
    return 'Wait for the current step to finish';
  }
  if (job.workflowState !== 'pending-merge' && job.workflowState !== 'warning') {
    return 'Only jobs pending merge can be merged';
  }
  if (job.totals.prOpened === 0) {
    return 'No open pull requests remain to merge';
  }
  if (currentUserRef && job.createdBy === currentUserRef) {
    return 'You cannot merge a job you created';
  }
  return undefined;
}

const OPERATION_LABELS: Record<string, string> = {
  'create-missing-file': 'Create missing file',
  'edit-existing-file': 'Edit existing file',
  'edit-catalog-info': 'Edit catalog-info.yaml',
};

export function buildJobChangePreview(job: BatchJobWorkflowItem): BatchJobChangePreview {
  const operationLabel = OPERATION_LABELS[job.operation] ?? job.operation;
  const repositoryCount = Math.max(
    job.totals.prOpened +
      job.totals.manualReview +
      job.totals.failed +
      job.totals.queued +
      job.totals.running,
    job.totals.prOpened,
  );
  const pullRequestCount = job.totals.prOpened;

  const requestBodyChanges =
    job.request?.body
      ?.split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0 && !line.startsWith('Opened from')) ?? [];

  const genericChanges: Record<string, string[]> = {
    'edit-catalog-info': [
      `Apply ${operationLabel.toLowerCase()} updates via pull request`,
      'Changes are scoped to catalog-info.yaml only',
      'Each repository receives its own pull request on a temporary branch',
    ],
    'edit-existing-file': [
      `Apply ${operationLabel.toLowerCase()} updates via pull request`,
      'Existing file contents are edited in place on a temporary branch',
      'No force pushes or default-branch writes are performed',
    ],
    'create-missing-file': [
      `Create the requested file via pull request where it does not exist`,
      'Repositories that already contain the file are skipped',
      'Each change is opened as its own pull request',
    ],
  };

  const warnings: string[] = [];
  if (job.totals.manualReview > 0) {
    warnings.push(`${job.totals.manualReview} repository(ies) need manual review before merge`);
  }
  if (job.totals.failed > 0) {
    warnings.push(`${job.totals.failed} repository(ies) failed during the open step`);
  }
  if (job.workflowState === 'warning' && job.totals.prOpened > 0) {
    warnings.push(
      `${job.totals.prOpened} pull request(s) still need to merge before this job can complete`,
    );
  }

  return {
    headline: job.title,
    operationLabel,
    repositoryCount: repositoryCount || pullRequestCount,
    pullRequestCount,
    changes:
      requestBodyChanges.length > 0
        ? requestBodyChanges
        : genericChanges[job.operation] ?? [
            `Run ${operationLabel.toLowerCase()} across ${repositoryCount || 'all'} repositories`,
          ],
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

export function getWorkflowAction(
  job: BatchJobWorkflowItem,
  currentUserRef?: string,
): BatchJobWorkflowAction {
  const activeSubjob = getActiveSubjob(job);

  if (activeSubjob?.type === 'approval') {
    return {
      label: 'Approving…',
      kind: 'approve',
      mode: 'status',
      loading: true,
      enabled: false,
      buttonColor: 'primary',
      buttonVariant: 'outlined',
    };
  }

  if (activeSubjob?.type === 'merge') {
    return {
      label: 'Merging…',
      kind: 'merge',
      mode: 'status',
      loading: true,
      enabled: false,
      buttonColor: 'primary',
      buttonVariant: 'outlined',
    };
  }

  if (job.workflowState === 'draft') {
    const opening =
      job.status === 'queued' ||
      job.status === 'running' ||
      (job.totals.prOpened === 0 && job.totals.queued + job.totals.running > 0);
    return {
      label: opening ? 'Opening…' : 'Open',
      kind: 'opening',
      mode: 'status',
      loading: opening,
      enabled: false,
      disabledReason: opening ? 'Pull requests are still being opened' : undefined,
      buttonColor: 'default',
      buttonVariant: 'outlined',
    };
  }

  if (job.workflowState === 'pending-approval') {
    const enabled = canApproveJob(job, currentUserRef);
    return {
      label: 'Approve',
      kind: 'approve',
      mode: 'action',
      action: 'approve',
      enabled,
      disabledReason: approvalDisabledReason(job, currentUserRef),
      loading: false,
      buttonColor: 'primary',
      buttonVariant: 'contained',
    };
  }

  if (job.workflowState === 'pending-merge' || job.workflowState === 'warning') {
    const enabled = canMergeJob(job, currentUserRef);
    return {
      label: job.workflowState === 'warning' ? 'Retry merge' : 'Merge',
      kind: 'merge',
      mode: 'action',
      action: 'merge',
      enabled,
      disabledReason: mergeDisabledReason(job, currentUserRef),
      loading: false,
      buttonColor: 'primary',
      buttonVariant: 'contained',
    };
  }

  if (job.workflowState === 'complete') {
    return {
      label: 'Complete',
      kind: 'complete',
      mode: 'status',
      enabled: false,
      loading: false,
      buttonColor: 'default',
      buttonVariant: 'outlined',
    };
  }

  if (job.workflowState === 'error') {
    return {
      label: 'Error',
      kind: 'error',
      mode: 'status',
      enabled: false,
      loading: false,
      buttonColor: 'secondary',
      buttonVariant: 'outlined',
    };
  }

  return {
    label: 'Canceled',
    kind: 'canceled',
    mode: 'status',
    enabled: false,
    loading: false,
    buttonColor: 'default',
    buttonVariant: 'outlined',
  };
}

export function buildWorkflowSteps(job: BatchJobWorkflowItem): BatchJobWorkflowStep[] {
  const approvalInProgress = job.workflowBusy === 'approval';
  const mergeInProgress = job.workflowBusy === 'merge';

  let openStatus: BatchJobWorkflowStepStatus = 'pending';
  if (job.workflowState === 'draft' && job.totals.prOpened === 0) {
    openStatus = job.status === 'running' || job.status === 'queued' ? 'running' : 'pending';
  } else if (job.totals.prOpened > 0 || job.workflowState !== 'draft') {
    openStatus = 'complete';
  }
  if (job.workflowState === 'error' || job.workflowState === 'canceled') {
    openStatus = job.totals.prOpened > 0 ? 'complete' : 'error';
  }

  let approvalStatus: BatchJobWorkflowStepStatus = 'pending';
  let approvalLog: string | undefined;
  if (approvalInProgress) {
    approvalStatus = 'running';
    approvalLog = 'Approval in progress…';
  } else if (job.workflowState === 'pending-approval') {
    approvalStatus = 'running';
    approvalLog = 'Waiting for an admin other than the job creator to approve';
  } else if (['pending-merge', 'complete'].includes(job.workflowState) || job.approvedBy) {
    approvalStatus = 'complete';
    approvalLog = job.approvedBy
      ? `Approved by ${formatBatchUserRef(job.approvedBy)}`
      : 'Approved';
  } else if (job.workflowState === 'error') {
    approvalStatus = 'error';
  }

  let mergeStatus: BatchJobWorkflowStepStatus = 'pending';
  let mergeLog: string | undefined;
  if (mergeInProgress) {
    mergeStatus = 'running';
    mergeLog = 'Merging pull requests…';
  } else if (job.workflowState === 'pending-merge') {
    mergeStatus = 'running';
    mergeLog = 'Ready to merge all open pull requests';
  } else if (job.workflowState === 'warning') {
    mergeStatus = 'warning';
    mergeLog =
      job.totals.prOpened > 0
        ? `${job.totals.prOpened} pull request(s) still open; retry merge or merge manually on GitHub`
        : 'Waiting to confirm every pull request has merged on GitHub';
  } else if (job.workflowState === 'complete') {
    mergeStatus = 'complete';
    mergeLog = job.mergedBy
      ? `Merged by ${formatBatchUserRef(job.mergedBy)}`
      : 'All pull requests merged';
  } else if (job.workflowState === 'error' && job.approvedBy) {
    mergeStatus = 'error';
  }

  let completeStatus: BatchJobWorkflowStepStatus = 'pending';
  let completeLog: string | undefined;
  if (job.workflowState === 'complete') {
    completeStatus = 'complete';
    completeLog = 'All steps finished successfully';
  } else if (job.workflowState === 'canceled') {
    completeStatus = 'skipped';
    completeLog = 'Job canceled';
  } else if (job.workflowState === 'error' && job.mergedBy) {
    completeStatus = 'error';
  }

  return [
    {
      id: 'open',
      label: 'Open',
      status: openStatus,
      log:
        job.totals.prOpened > 0
          ? `Opened ${job.totals.prOpened} pull request(s) across repositories`
          : undefined,
    },
    {
      id: 'approval',
      label: 'Approval',
      status: approvalStatus,
      log: approvalLog,
    },
    {
      id: 'merge',
      label: 'Merge',
      status: mergeStatus,
      log: mergeLog,
    },
    {
      id: 'complete',
      label: 'Complete',
      status: completeStatus,
      log: completeLog,
    },
  ];
}

export function buildWorkflowLog(job: BatchJobWorkflowItem): string {
  const lines = buildWorkflowSteps(job)
    .filter(step => step.log)
    .map(step => `[${step.label}] ${step.log}`);
  return lines.length > 0 ? lines.join('\n') : 'No workflow events yet.';
}

type LegacyBatchJob = {
  id: string;
  status: string;
  title: string;
  operation: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  workflowState?: BatchJobWorkflowState;
  workflowBusy?: 'approval' | 'merge';
  approvedBy?: string;
  mergedBy?: string;
  request?: BatchJobWorkflowItem['request'];
  totals: BatchJobWorkflowItem['totals'];
};

function deriveWorkflowStateFromLegacyStatus(job: LegacyBatchJob): BatchJobWorkflowState {
  switch (job.status) {
    case 'queued':
    case 'running':
      return 'draft';
    case 'cancelled':
      return 'canceled';
    case 'failed':
      return 'error';
    case 'completed-with-errors':
      return 'error';
    case 'completed':
      return job.totals.prOpened > 0 ? 'pending-approval' : 'complete';
    default:
      return 'draft';
  }
}

export function mapLegacyJobToWorkflow(job: LegacyBatchJob): BatchJobWorkflowItem {
  const workflowState = job.workflowState ?? deriveWorkflowStateFromLegacyStatus(job);

  return {
    id: job.id,
    title: job.title,
    operation: job.operation,
    createdBy: job.createdBy,
    createdAt: job.createdAt,
    updatedAt: job.updatedAt,
    workflowState,
    workflowBusy: job.workflowBusy,
    approvedBy: job.approvedBy,
    mergedBy: job.mergedBy,
    request: job.request,
    totals: job.totals,
    status: job.status,
  };
}
